import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { fetchInvoices, fetchCustomers, fetchUpdateInvoiceDueDate, updateInvoiceStatus } from '../../utils/api/api';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import Modal from '../../elements/Modal';
import { FaEye } from 'react-icons/fa6';
import { FaRegEdit } from 'react-icons/fa';
import moment from 'moment';

const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [invoicesPerPage] = useState(40);
  const [totalPages, setTotalPages] = useState(1);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [newDueDate, setNewDueDate] = useState('');
  const [statusModalIsOpen, setStatusModalIsOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const openStatusModal = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setStatusModalIsOpen(true);
  };

  const handleStatusChange = async () => {
    setLoading(true);
    try {
      await updateInvoiceStatus({ id: selectedInvoiceId, status: newStatus });
      setStatusModalIsOpen(false);
      toast.success("status updated")
    } catch (error) {
      console.error("Error updating invoice status:", error);
      toast.error(error || "Failed to update")
    } finally {
      setLoading(false);
    }
  };

  const getCustomers = async () => {
    try {
      const response = await fetchCustomers({
        page: 1,
        pageSize: 600,
      });
      setCustomers(response.customers.map(c => ({ value: c.id, label: `${c.first_name} ${c.last_name}` })));
    } catch (error) {
      toast.error('Failed to get customers');
    }
  };

  const openModal = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setModalIsOpen(true);
  };

  const getInvoices = async (page, size) => {
    setLoading(true);
    try {
      const payload = {
        page,
        pageSize: size,
        customer_id: selectedCustomer ? selectedCustomer.value : undefined,
        payment_status: paymentStatus || undefined, // Add payment status filter if selected
      };
      const response = await fetchInvoices(payload);
      setInvoices(response.invoices);
      setFilteredInvoices(response.invoices);
      setTotalPages(response.meta.totalPages);
    } catch (error) {
      toast.error('Failed to get invoices');
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInvoices(currentPage, invoicesPerPage);
    getCustomers();
  }, [currentPage, selectedCustomer, paymentStatus]);

  const handleCustomerSelect = (selectedOption) => {
    setSelectedCustomer(selectedOption);
    setCurrentPage(1);
  };

  const handlePaymentStatusSelect = (e) => {
    setPaymentStatus(e.target.value);
    setCurrentPage(1);
  };

  const indexOfLastInvoice = currentPage * invoicesPerPage;

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleDueDateChange = (invoiceId) => {
    const payload = {
      id: invoiceId,
      payment_due_date: newDueDate,
    };
    fetchUpdateInvoiceDueDate(payload)
      .then(() => {
        toast.success('Due date updated successfully');
        getInvoices(currentPage, invoicesPerPage);
      })
      .catch((error) => {
        toast.error('Failed to update due date');
        console.error(error);
      });
  };

  return (
    <div className="p-4 bg-white rounded-lg">
      <h2 className="mb-4 text-xl font-bold">Invoice List</h2>
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="w-[250px]">
          <Select
            options={customers}
            value={selectedCustomer}
            onChange={handleCustomerSelect}
            placeholder="Select a customer"
            className="w-full text-gray-600 font-light text-[14px] rounded-md outline-none focus:border-secondary"
          />
        </div>

        <select
          className="w-[200px] p-2 border border-gray-300 rounded-md bg-transparent outline-none"
          value={paymentStatus}
          onChange={handlePaymentStatusSelect}
        >
          <option value="">All Payment Statuses</option>
          <option value="pending">Pending</option>
          <option value="partially_paid">Partially Paid</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center">Loading...</div>
      ) : invoices.length === 0 ? (
        <div className="text-center text-gray-500">No invoices available</div>
      ) : (
        <div className="relative min-h-screen bg-gray-100 py-10 md:px-6">
          <div className="absolute left-10 top-0 h-full border-l-2 border-yellow-500"></div>

          <div className="space-y-8 md:pl-16">
            {filteredInvoices
              .sort((a, b) => new Date(a.payment_due_date) - new Date(b.payment_due_date))
              .map((invoice) => (
                <div key={invoice.id} className="relative flex items-center gap-4 bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-6 h-6 bg-yellow-500 rounded-full ring-4 ring-gray-100"></div>

                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <p className="text-lg font-semibold text-gray-800">
                        {invoice.customer.first_name} {invoice.customer.last_name}
                      </p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${invoice.payment_status === 'paid' ? 'bg-green-100 text-green-600' : invoice.payment_status === 'overdue' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'}`}>
                        {invoice.payment_status.charAt(0).toUpperCase() + invoice.payment_status.slice(1)}
                      </span>
                    </div>

                    <div className="mt-2 text-[13px] text-gray-600">
                      <p><strong>Invoice No:</strong> {invoice.serial_no}</p>
                      <p><strong>Order ID:</strong> {invoice.order_id}</p>
                      <p><strong>Invoice Due Date:</strong> {moment(invoice.payment_due_date).format("MMM DD, YYYY")}</p>
                      <p><strong>Amount:</strong> KES {Number(invoice.total_amount).toLocaleString("KES")}</p>
                    </div>

                    <div className="mt-4 flex flex-wrap md:items-center gap-4">
                      <Link
                        to={`/dashboard/invoices/${invoice.id}`}
                        className="text-white bg-secondary py-1 px-4 rounded-md hover:text-primary flex items-center border border-secondary gap-2 text-[13px]"
                      >
                        View <FaEye size={20} />
                      </Link>
                      <button
                        onClick={() => openStatusModal(invoice.id)}
                        className="text-white bg-blue-500 py-1 px-4 rounded-md hover:text-primary flex items-center border border-blue-500 text-[13px] gap-2"
                      >
                        Status <FaRegEdit size={14} />
                      </button>
                      <button
                        onClick={() => openModal(invoice.id)}
                        className="text-white bg-green-500 py-1 px-4 rounded-md hover:text-primary flex items-center border border-green-500 text-[13px] gap-2"
                      >
                        Date <FaRegEdit size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {filteredInvoices.length > 0 && (
            <>
              <div className="flex justify-end w-full mt-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="text-[14px] flex mr-1 items-center text-primary font-light gap-2"
                >
                  Prev
                </button>

                {totalPages > 5 && currentPage > 3 && (
                  <>
                    <button
                      onClick={() => paginate(1)}
                      className={`px-3 py-1.5 mx-1 text-[14px] font-medium ${currentPage === 1 ? 'bg-primary text-white' : 'text-primary'} rounded-md`}
                    >
                      1
                    </button>
                    <span className="px-3 py-1.5 mx-1 text-[14px] font-medium text-primary">...</span>
                  </>
                )}

                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => paginate(page)}
                        className={`px-3 py-1.5 mx-1 text-[14px] font-medium ${currentPage === page ? 'bg-primary text-white' : 'text-primary'} rounded-md`}
                      >
                        {page}
                      </button>
                    );
                  } else {
                    return null;
                  }
                })}

                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span className="px-3 py-1.5 mx-1 text-[14px] font-medium text-primary">...</span>
                    <button
                      onClick={() => paginate(totalPages)}
                      className={`px-3 py-1.5 mx-1 text-[14px] font-medium ${currentPage === totalPages ? 'bg-primary text-white' : 'text-primary'} rounded-md`}
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="text-[14px] flex ml-1 items-center text-primary font-light gap-2"
                >
                  Next
                </button>
              </div>
            </>
          )}

          {modalIsOpen && (
            <Modal onClose={() => setModalIsOpen(false)} title="Update Due Date">
              <div>
                <h2 className="text-xl font-semibold">Update Due Date</h2>
                <input
                  type="date"
                  value={newDueDate}
                  onChange={(e) => setNewDueDate(e.target.value)}
                  className="mt-4 p-2 border border-gray-300 rounded-md w-full"
                />
                <div className="mt-4 flex justify-end gap-4">
                  <button
                    onClick={() => {
                      handleDueDateChange(selectedInvoiceId);
                      setModalIsOpen(false);
                    }}
                    className="bg-primary text-white px-4 py-2 rounded-md"
                  >
                    Update
                  </button>
                  <button
                    onClick={() => setModalIsOpen(false)}
                    className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      )}
      {statusModalIsOpen && (
        <Modal onClose={() => setStatusModalIsOpen(false)} title="Update Invoice Status">
          <div>
            <h2 className="text-[13px] text-gray-600 ">Select invoice status, and update </h2>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="mt-4 p-2 border border-gray-300 rounded-md w-full"
            >
              <option value="" disabled>Select Status</option>
              <option value="pending">Pending</option>
              <option value="partially_paid">Partially Paid</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
            <div className="mt-4 flex justify-end gap-4">
              <button
                onClick={handleStatusChange}
                disabled={loading}
                className="bg-secondary text-white px-4 py-2 rounded-md"
              >
                {loading ? 'Updating...' : 'Update'}
              </button>
              <button
                onClick={() => setStatusModalIsOpen(false)}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default InvoiceList;
